import { FlakinessReport } from '@flakiness/flakiness-report';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK, xxHashObject } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { Temporal } from 'temporal-polyfill';
import { api } from './api.js';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { FKGuideArea } from './components/fk-guide.js';
import { FKHeadSelectEvent } from './components/head-selector.js';
import { ProjectContext } from './components/project-context.js';
import { createTestHealthCalendarState, TestHealthCalendarLinkifier } from './components/test-health-calendar.js';
import { consume, contexts } from './contexts.js';
import { welcome_new_project } from './docs/welcome_new_project.js';
import { FeatureTestsReport, FeatureTestsReportState } from './features/feature-tests-report.js';
import { markdownStyles } from './markdown.js';
import { docsUrl, projectPlatformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { tasks } from './tasks.js';
import { URLOptions } from './urlOptions.js';

const pageSchema = {
  query: URLOptions.query,
  /* report time interval options */
  head: URLOptions.head,
  since: URLState.option<Temporal.PlainDate|undefined>({
    name: 'since',
    default: undefined,
    encode: d => d?.toString(),
    decode: txt => txt ? Temporal.PlainDate.from(txt) : undefined,
  }),
  until: URLOptions.until,
  viewSince: URLOptions.viewSince,
  viewUntil: URLOptions.viewUntil,

  timelineSplit: URLOptions.timelineSplit,

  /* ---- */
  plane: URLOptions.plane,
  page: URLOptions.page,
  pageSize: URLOptions.pageSize,
  sortName: URLOptions.sortName,
  sortDirection: URLOptions.sortDirection,
  historyType: URLOptions.historyType,
};

@customElement('page-project-tests')
export class PageProjectTests extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project',
        '/:org/:project/',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-tests
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-tests>
      `),
    }];
  }

  static url(options: Omit<FeatureTestsReportState, 'timelineSplit'> & {
    orgSlug: string,
    projectSlug: string,
    since?: Temporal.PlainDate,
    until?: Temporal.PlainDate,
    viewSince?: Temporal.PlainDate,
    viewUntil?: Temporal.PlainDate,
    timelineSplit?: EnvGroup,
    head?: string,
    snapshotId: WireTypes.DataSnapshotId | undefined,
  }) {
    const pathname = ['', options.orgSlug, options.projectSlug].join('/');
    return projectPlatformUrl(pathname, URLState.toSearchParams(pageSchema, {
      since: options.since,
      until: options.until,
      head: options.head,
      viewSince: options.viewSince,
      viewUntil: options.viewUntil,
      timelineSplit: options.timelineSplit,
      page: options.page,
      pageSize: options.pageSize,
      plane: options.plane,
      query: options.query,
      sortDirection: options.sortDirection,
      sortName: options.sortName,
      historyType: options.historyType,
    }), options.snapshotId);
  }

  private _urlState = new URLState(this, pageSchema);

  @consume({ context: contexts.user, subscribe: true }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.projectReferences, subscribe: true }) private _branches?: ContextType<typeof contexts.projectReferences>;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property() orgSlug?: string;
  @property() projectSlug?: string;

  @query('head-selector') private _headSelector?: HTMLElement;
  @query('timeline-selector') private _timelineSelector?: HTMLElement;
  @query('test-health-calendar') private _testHealthCalendar?: HTMLElement;
  @query('feature-tests-report') private _testsReport?: FeatureTestsReport;

  private _guideAreas: FKGuideArea[] = [{
    target: () => this._headSelector,
    title: 'Branch selector',
    placement: 'bottom-start',
    description: html`
      <p>
        The selected branch determines the history shown on this page.
        Test results, the health calendar, and available environments all reflect that branch.
      </p>
    `,
  }, {
    target: () => this._timelineSelector,
    title: 'Environment selector',
    placement: 'bottom-start',
    description: html`
      <p>
        An environment is the setup a test runs in, such as its operating system or browser.
        The selected environments filter the test results and health calendar.
      </p>
    `,
  }, {
    target: () => this._testHealthCalendar,
    title: 'Branch health calendar',
    placement: 'left-start',
    description: html`
      <p>
        Each day stands for the commits made that day on the selected branch.
        Its color reflects the status of the test suite <em>by the end of the day</em>,
        across the selected environments.
        A test run counts toward the day of its commit, even if it ran days later.
        A single failed test turns the day red, but a test that failed in the morning
        and was fixed by evening doesn't. Selecting a day shows its test results.
      </p>
    `,
  }, {
    target: () => this._testsReport?.fqlSearchElement,
    title: 'Search',
    placement: 'bottom-start',
    href: docsUrl('/api/fql/'),
    description: html`
      <fql-help></fql-help>
      <p>The status cards below count only the tests that match.</p>
    `,
  }, {
    target: () => this._testsReport?.reportOutcomesElement,
    title: 'Status cards',
    placement: 'bottom-start',
    description: html`
      <p>Each card counts the tests with that status on the selected day.</p>
      <p>A test is <b>reliable</b> if it had no failures or flakes in its last 10 tested commits. A reliable test that fails counts as a <b>regression</b>, since a recent change likely broke it. Any other failing test counts as a <b>failure</b>.</p>
      <p>Click a card to filter the table to its tests.</p>
    `,
  }, {
    target: () => this._testsReport?.testsTableElement,
    title: 'Tests table',
    placement: 'top-start',
    description: html`
      <p>The table shows one row per test and environment. The History column shows the test's recent results. Selecting a bar opens the test's history.</p>
      <p>The tabs group the same tests by environment, error, tag, or annotation.</p>
    `,
  }];

  private _headRefTask = tasks.headRef(this, {
    project: () => this._project,
    snapshotId: () => this._snapshotId,
    defaultBranch: () => this._branches?.defaultBranch,
    head: () => this._urlState.state.head,
  });

  private _reportTimeInterval = new Task(this, {
    args: () => [
      this._project, this._snapshotId, this._headRefTask.value, this._urlState.state.since?.toString(), this._urlState.state.until?.toString(),
    ] as const,
    task: async([project, snapshotId, headRef, sinceTimestamp, untilTimestamp], { signal }) => {
      CHECK(project && snapshotId && headRef);
      // If either since or until is not set, default to the day of the last tested
      // commit. The branch head may be an untested commit from a newer day.
      if (!sinceTimestamp || !untilTimestamp) {
        const lastTestedCommit = await api.history.lastTestedCommit.POST({
          orgSlug: project.org.orgSlug,
          projectSlug: project.projectSlug,
          snapshotId,
          head: headRef.commit.commitId,
          split: EnvGroup.open().serialize(),
        }, { signal });
        const defaultCommit = lastTestedCommit ?? headRef.commit;
        const defaultDay = Temporal.Instant.fromEpochMilliseconds(defaultCommit.timestamp)
          .toZonedDateTimeISO(project.timezone)
          .toPlainDate();
        return {
          since: defaultDay,
          until: defaultDay,
        }
      }
      // Otherwise, just the since and until that we're given.
      return {
        since: Temporal.PlainDate.from(sinceTimestamp),
        until: Temporal.PlainDate.from(untilTimestamp),
      }
    }
  });

  private _commitOptionsTask = new Task(this, {
    args: () => [
      this._project,
      this._headRefTask.value,
      this._reportTimeInterval.value,
    ] as const,
    task: async ([project, headRef, reportTimeInterval], { signal }) => {
      CHECK(project && headRef && reportTimeInterval);
      const since = reportTimeInterval.since.toZonedDateTime(project.timezone);
      const until = reportTimeInterval.until.add({ days: 1 }).toZonedDateTime(project.timezone);
      return {
        head: headRef.name,
        sinceTimestamp: since.epochMilliseconds as FlakinessReport.UnixTimestampMS,
        untilTimestamp: until.epochMilliseconds as FlakinessReport.UnixTimestampMS,
      } satisfies WireTypes.ListCommitOptions;
    }
  });

  private _allEnvironments = tasks.allEnvironments(this, {
    commitOptions: () => this._commitOptionsTask.value,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  private _testHealthCalendarLinker = new Task(this, {
    args: () => [
      window.location.href,
    ] as const,
    task: async ([], { signal }) => {
      const pageLinker = this._urlState.createLinkRenderer();

      const calendarLinker: TestHealthCalendarLinkifier = (options) => {
        return pageLinker({
          since: options.selectedSince,
          until: options.selectedUntil,
          viewSince: options.viewportSince,
          viewUntil: options.viewportUntil,
          page: 0,
        });
      }
      return calendarLinker;
    },
  });

  private _testHealthCalendarStateTask = new Task(this, {
    args: () => [this._reportTimeInterval.value?.since, this._reportTimeInterval.value?.until, this._urlState.state.viewSince, this._urlState.state.viewUntil],
    argsEqual: (oldArgs, newArgs) => xxHashObject(oldArgs) === xxHashObject(newArgs),
    task: async([since, until, viewSince, viewUntil]) => {
      return createTestHealthCalendarState({
        selectedSince: since,
        selectedUntil: until,
        viewportSince: viewSince,
        viewportUntil: viewUntil,
      });
    }
  });

  // "All Environments" of the head's history.
  private _defaultSplit = tasks.defaultSplit(this, {
    preset: () => this._headRefTask.value ? { type: 'commit', commitId: this._headRefTask.value.commit.commitId } : undefined,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  // Once the default is known, a URL that spells it out loses it, without a new history entry.
  private _canonicalUrlTask = new Task(this, {
    args: () => [this._urlState.state.timelineSplit, this._defaultSplit.value] as const,
    task: async ([split, defaultSplit]) => {
      if (split && defaultSplit && split.isEqual(defaultSplit))
        this._urlState.update({ timelineSplit: undefined }, { replace: true });
    },
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug, this._project] as const,
    metadata: ([orgSlug, projectSlug, project]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug,
      testsCount: project?.snapshot ? TestOutcomes.sumAllCounts(project.snapshot.defaultReport.testOutcomes) : undefined,
    }) : undefined,
  });

  override render() {
    return html`
      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"tests"}
      ></project-header>
      ${this._project?.runsCount === 0 ? this._renderNoReportsScreen(this._project) : this._renderContent()}
      <app-footer></app-footer>
    `;
  }

  private _renderContent() {
    const project = this._project;
    const { since, until } = this._reportTimeInterval.value ?? {};
    const split = this._urlState.state.timelineSplit ?? this._defaultSplit.value;
    if (!project)
      return nothing;

    return html`
      <project-body wide>
        ${since && until && split ? html`

        
        <feature-tests-report
          .commitOptions=${this._commitOptionsTask.value}
          .viewSince=${this._urlState.state.viewSince}
          .viewUntil=${this._urlState.state.viewUntil}
          .until=${this._urlState.state.until}
          .state=${{
            timelineSplit: split,
            page: this._urlState.state.page,
            pageSize: this._urlState.state.pageSize,
            plane: this._urlState.state.plane,
            query: this._urlState.state.query,
            sortDirection: this._urlState.state.sortDirection,
            sortName: this._urlState.state.sortName,
            historyType: this._urlState.state.historyType,
          } satisfies FeatureTestsReportState}
          .linkifier=${(options: FeatureTestsReportState) => PageProjectTests.url({
            ...options,
            orgSlug: project.org.orgSlug,
            projectSlug: project.projectSlug,
            snapshotId: this._snapshotId,
            since: this._urlState.state.since,
            until: this._urlState.state.until,
            viewSince: this._urlState.state.viewSince,
            viewUntil: this._urlState.state.viewUntil,
            head: this._urlState.state.head,
          })}
        >
          <h-box slot=header>
            <head-selector
              .head=${this._headRefTask.value}
              @fk-select=${(event: FKHeadSelectEvent) => {
                this._urlState.update({ head: event.head.name });
              }}
            ></head-selector>
            <timeline-selector multiple
              .envs=${this._allEnvironments.value}
              .split=${split}
              .defaultSplit=${this._defaultSplit.value}
              @fk-select=${(event: CustomEvent<EnvGroup>) => {
                this._urlState.update({ timelineSplit: event.detail, page: 0 });
              }}
            ></timeline-selector>
            <fk-guide label="Tests page guide" .areas=${this._guideAreas}></fk-guide>
          </h-box>
          <test-health-calendar slot=sidebar
            style="align-self: start;"
            .linkifier=${this._testHealthCalendarLinker.value}
            .split=${split}
            .head=${this._headRefTask.value}
            .state=${this._testHealthCalendarStateTask.value}
            .hideRangeButtons=${true}
          ></test-health-calendar>
        </feature-tests-report>
        ` : nothing}
      </project-body>
    `
  }

  private _renderNoReportsScreen(project: WireTypes.Project) {
    return html`
      <project-body>
        <div class=markdown-body>
          ${welcome_new_project(project)}
        </div>
      </project-body>
    `;
  }

  static styles = [...markdownStyles, pageStyles, linkStyles, css`
    .markdown-body {
      margin-top: var(--sl-spacing-medium);
    }
  `]
}
